import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../auth.service';
import { AppEventsService } from '../../analytics/app-events.service';
import { inferAnalyticsSource } from '../../analytics/infer-analytics-source';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { CheckCircle, LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-auth-successful',
  standalone: true,
  imports: [CommonModule, RouterLink, LucideAngularModule],
  templateUrl: './auth-successful.html',
  styleUrl: './auth-successful.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthSuccessfulComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly appEvents = inject(AppEventsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly icons = {
    CheckCircle,
  };

  ngOnInit(): void {
    this.handleAuthRedirect();
  }

  private handleAuthRedirect(): void {
    this.route.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) => {
          const token = params['token'];

          if (!token) {
            console.error('No token found in URL');
            return of(false);
          }

          return this.authService.validateToken(token);
        }),
      )
      .subscribe({
        next: (isValid) => {
          if (isValid) {
            // Session linking is handled by AuthService.validateToken()
            this.redirectAfterAuth();
          } else {
            this.router.navigate(['/']);
          }
        },
        error: (error) => {
          console.error('Error during authentication:', error);
          this.router.navigate(['/']);
        },
      });
  }

  private redirectAfterAuth(): void {
    const params = this.route.snapshot.queryParams;
    const isNewUser = params['isNewUser'] === 'true';
    const redirectUrl = params['redirect'] || null;
    const eventId = params['eventId'] || undefined;

    if (isNewUser) {
      // Infer analytics source from the redirect URL pattern
      const source = inferAnalyticsSource(redirectUrl);
      this.appEvents.signUpCompleted(source, eventId);
    }

    // If a redirectUrl was provided, go there; otherwise let RoleRedirectComponent handle it
    this.router.navigateByUrl(redirectUrl ?? '/dashboard');
  }
}
